import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, Typography, useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { Datagrid, TextField, FunctionField, useTranslate } from 'react-admin';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { errorLevelChoices } from '@/components/global/options/selectOptions.jsx';
import SimpleList from '@/components/global/list/SimpleList.jsx';
import CustomTypographyWithTooltip from '@/components/global/fields/CustomTypographyWithTooltip.jsx';
import { styled } from '@mui/material/styles';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { DeviceErrorPopper } from '@/components/device/DeviceErrorPopper.jsx';

const StyledDeviceErrorReportList = styled('div')(({ shouldExtend }) => ({
    '& .solution-tip': {
        display: 'block',
        width: '326px',
    },
    '& .text-nowrap': {
        whiteSpace: 'nowrap',
    },
    '& .custom-select-array-input.always-on-filter': {
        '& > .MuiFormControl-root': {
            minWidth: 'min-content',
        },
        '& .checkboxes-filter__popup': {
            '& > .MuiList-root': {
                overflow: 'auto',
                maxHeight: '195px',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    maxHeight: '178px',
                },
                '& .MuiList-root': {
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        overflow: 'hidden',
                    },
                },
            },
            '& .actions': {
                position: 'relative',
            },
        },
    },
    ...(shouldExtend && {
        '& .MuiPaper-root': {
            marginRight: '-20px',
            [`@media (min-width: ${breakpoints.values.md}px)`]: {
                marginRight: '-56px',
            },
        },
        '& .RaDatagrid-tableWrapper': {
            borderRadius: '16px 0 0 0',
        },
    }),
}));

const filters = ({ translate }) => [
    <CustomSelectArrayInput
        key='filter_level'
        source='level_in'
        label={translate('resources.deviceError.labels.level')}
        choices={errorLevelChoices}
        alwaysOn
        fullWidth
        alwaysDropdown={true}
    />,
];

const DeviceErrorReportList = ({ deviceId, shouldExtend = false }) => {
    const translate = useTranslate();

    return (
        <StyledDeviceErrorReportList shouldExtend={shouldExtend}>
            <SimpleList
                resource='device-error-report'
                filter={{ deviceId }}
                filters={filters({ translate })}
                filterDefaultValues={{ level_in: [1, 2] }}
                sort={{ field: 'time', order: 'DESC' }}
                empty={<Typography>{translate('resources.deviceError.labels.noErrors')}</Typography>}
                hasCreate={false}
                filtersAlwaysOn={true}
            >
                <Datagrid bulkActionButtons={false}>
                    <FunctionField source='code' label={translate('resources.contractor.fields.code')} render={record => <ErrorCodeField record={record} />} />
                    <FunctionField
                        source='time'
                        label={translate('resources.deviceError.labels.time')}
                        render={record =>
                            record?.time ? (
                                <Tooltip title={formatIntervalSince(record.time)} arrow classes={{ tooltip: 'tooltip-black' }}>
                                    <span className='text-nowrap'>{format(new Date(record.time), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                ''
                            )
                        }
                    />
                    <TextField className='text-nowrap' source='title' label={translate('resources.deviceError.labels.friendlyName')} />
                    <TextField source='level' label={translate('resources.deviceError.labels.level')} />
                    <TextField source='name' label={translate('resources.deviceError.labels.codeName')} />
                    <FunctionField
                        source='solutionTip'
                        label={translate('resources.deviceError.labels.suggestions')}
                        render={record => <CustomTypographyWithTooltip text={record?.solutionTip} />}
                    />
                </Datagrid>
            </SimpleList>
        </StyledDeviceErrorReportList>
    );
};

const ErrorCodeField = ({ record = {} }) => {
    const errorLevelNumber = record.level ?? 2;
    const errorLevel = errorLevelChoices.find(e => e.id === errorLevelNumber);
    const errorLevelName = errorLevel.name.charAt(0).toUpperCase() + errorLevel.name.slice(1);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [anchorEl, setAnchorEl] = useState(null);

    const transformedRecord = {
        error: {
            code: record.code,
            level: record.level,
            title: record.title,
            name: record.name,
            solutionTip: record.solutionTip,
            time: record.time,
            details: record.details || '',
        },
    };

    const handleMouseEnter = event => {
        if (!isDesktop) return;
        setAnchorEl(event.currentTarget.closest('.error-field-container'));
    };

    const handleMouseLeave = () => {
        if (!isDesktop) return;
        setAnchorEl(null);
    };
    return (
        <div
            className='error-field-container'
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'left', position: 'relative', width: 'fit-content' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Chip
                size='small'
                label={
                    <span style={{ fontWeight: '600' }}>
                        {errorLevelName}: {record.code}
                    </span>
                }
                sx={{ backgroundColor: errorLevel.color }}
            />
            {isDesktop && <DeviceErrorPopper open={Boolean(anchorEl)} anchorEl={anchorEl} record={transformedRecord} onMouseLeave={handleMouseLeave} />}
        </div>
    );
};

DeviceErrorReportList.propTypes = {
    deviceId: PropTypes.string.isRequired,
};

ErrorCodeField.propTypes = {
    record: PropTypes.shape({
        level: PropTypes.number,
        code: PropTypes.number,
    }),
};

export default DeviceErrorReportList;
